import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import SettingsIcon from '@mui/icons-material/Settings'
import SearchableSelect from 'components/SearchableSelect'

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

const TaskSettingsModal = ({ open, onClose, settings, onSave, loading }) => {
  const [reportingPeriod, setReportingPeriod] = useState('quarterly')
  const [startMonth, setStartMonth] = useState(1)

  useEffect(() => {
    if (settings) {
      setReportingPeriod(settings.reportingPeriod || 'quarterly')
      setStartMonth(settings.reportingPeriodStartMonth || 1)
    }
  }, [settings])

  if (!open) return null

  const handleSave = () => {
    onSave({
      reportingPeriod,
      reportingPeriodStartMonth: startMonth
    })
  }

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalIcon><SettingsIcon /></ModalIcon>
          <div>
            <ModalTitle>Task Settings</ModalTitle>
            <ModalSubtitle>Configure reporting period for this Building Passport</ModalSubtitle>
          </div>
        </ModalHeader>

        <ModalBody>
          <FieldGroup>
            <FieldLabel>Reporting Period</FieldLabel>
            <RadioGroup>
              <RadioOption
                $active={reportingPeriod === 'monthly'}
                onClick={() => setReportingPeriod('monthly')}
              >
                <RadioDot $active={reportingPeriod === 'monthly'} />
                <RadioLabel>
                  <RadioTitle>Monthly</RadioTitle>
                  <RadioDesc>Period resets each calendar month</RadioDesc>
                </RadioLabel>
              </RadioOption>
              <RadioOption
                $active={reportingPeriod === 'quarterly'}
                onClick={() => setReportingPeriod('quarterly')}
              >
                <RadioDot $active={reportingPeriod === 'quarterly'} />
                <RadioLabel>
                  <RadioTitle>Quarterly</RadioTitle>
                  <RadioDesc>Period resets every 3 months</RadioDesc>
                </RadioLabel>
              </RadioOption>
            </RadioGroup>
          </FieldGroup>

          {reportingPeriod === 'quarterly' && (
            <FieldGroup>
              <FieldLabel>Quarter Start Month</FieldLabel>
              <FieldHint>The month when Q1 begins each year</FieldHint>
              <SearchableSelect
                value={startMonth}
                onChange={(v) => setStartMonth(v)}
                options={monthNames.map((name, i) => ({ value: i + 1, label: name }))}
                ariaLabel='Quarter start month'
              />
            </FieldGroup>
          )}
        </ModalBody>

        <ModalFooter>
          <CancelBtn onClick={onClose}>Cancel</CancelBtn>
          <SaveBtn onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : 'Save Settings'}
          </SaveBtn>
        </ModalFooter>
      </Modal>
    </Overlay>
  )
}

export default TaskSettingsModal

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 440px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 24px 24px 0;
`

const ModalIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.lightGrey || '#f5f5f5'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.haiti || '#0C1229'};
  svg { font-size: 22px; }
`

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti || '#0C1229'};
  margin: 0;
`

const ModalSubtitle = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin: 2px 0 0;
`

const ModalBody = styled.div`
  padding: 24px;
`

const FieldGroup = styled.div`
  margin-bottom: 20px;
  &:last-child { margin-bottom: 0; }
`

const FieldLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti || '#0C1229'};
  margin-bottom: 8px;
`

const FieldHint = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin: -4px 0 8px;
`

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const RadioOption = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid ${({ $active }) => $active ? '#0ea5e9' : '#e5e7eb'};
  border-radius: 8px;
  cursor: pointer;
  background: ${({ $active }) => $active ? '#f0f9ff' : 'white'};
  transition: all 0.15s;

  &:hover {
    border-color: ${({ $active }) => $active ? '#0ea5e9' : '#cbd5e1'};
  }
`

const RadioDot = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${({ $active }) => $active ? '#0ea5e9' : '#d1d5db'};
  background: ${({ $active }) => $active ? '#0ea5e9' : 'white'};
  position: relative;
  flex-shrink: 0;
  margin-top: 1px;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: white;
    opacity: ${({ $active }) => $active ? 1 : 0};
  }
`

const RadioLabel = styled.div``

const RadioTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti || '#0C1229'};
`

const RadioDesc = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-top: 2px;
`

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 0 24px 24px;
`

const CancelBtn = styled.button`
  padding: 10px 20px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.haiti || '#0C1229'};
  &:hover { background: #f9fafb; }
`

const SaveBtn = styled.button`
  padding: 10px 20px;
  background: #0ea5e9;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  cursor: pointer;

  &:hover:not(:disabled) { background: #0284c7; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`
